<template>
  <div
    class="in-console-sidebar d-flex flex-column"
    :class="{ 'in-console-sidebar--show': sidebarIsVisible }"
    v-if="isEnabled"
  >
    <div
      class="in-console-sidebar__title-actions align-items-center justify-content-between"
      :class="{
        'in-console-sidebar__title-actions--visible': sidebarIsVisible,
      }"
    >
      <p class="in-console-sidebar__title">
        <span v-if="showing === 'in-console-messages'">
          {{ t("NOTIFICATIONS") }}
        </span>
        <span v-else>
          {{ t("HELP_AND_SUPPORT") }}
        </span>
      </p>
      <a
        href="javascript:"
        @click="closeInConsoleMessageSidebarState"
        class="in-console-sidebar__close"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="15"
          height="14"
          viewBox="0 0 15 14"
          fill="none"
        >
          <path
            d="M1.64756 14L0.247559 12.6L5.84756 7L0.247559 1.4L1.64756 0L7.24756 5.6L12.8476 0L14.2476 1.4L8.64756 7L14.2476 12.6L12.8476 14L7.24756 8.4L1.64756 14Z"
            fill="#666666"
          />
        </svg>
      </a>
    </div>
    <div
      class="in-console-sidebar__messages-container"
      :class="{
        'in-console-sidebar__messages-container--visible': sidebarIsVisible,
      }"
    >
      <div v-if="isLoading">
        <Spinner />
      </div>
      <div
        class="in-console-sidebar__messages-container__messages-list"
        :class="{
          'in-console-sidebar__messages-list--visible': sidebarIsVisible,
        }"
        v-else-if="inConsoleContent?.length > 0"
      >
        <InConsoleArticle
          v-for="message in inConsoleContent"
          :key="message.id"
          :message="message"
        />
      </div>
      <div class="in-console-sidebar__messages-container__empty" v-else>
        <p>
          {{ t("EMPTY_NOTIFICATIONS_TITLE") }}
        </p>
        <p>
          {{ t("EMPTY_NOTIFICATIONS_MESSAGE") }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  computed,
  ComputedRef,
  nextTick,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  watch,
  watchEffect,
} from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import { AxiosResponse } from "axios";
import { getInConsoleMessages } from "@/apps/merchant3/services/inConsoleMessages.service";
import InConsoleArticle from "@/apps/merchant3/components/in-console/articles/InConsoleArticle.vue";
import { IConsoleArticle } from "@/apps/merchant3/types/in-console/InConsoleArticle";
import Spinner from "@/apps/merchant3/shared-components/ui/Spinner.vue";
import IMerchant from "@/apps/merchant3/models/IMerchant";
import { getTrainingArticles } from "@/apps/merchant3/services/in-console/training/trainingArticles.service";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import useFeatureFlag from "@/apps/merchant3/hooks/useFeatureFlag";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import { SidebarContentTypes } from "@/apps/merchant3/types/in-console/SidebarContentTypes";
import logEvent from "@/apps/merchant3/services/event-logs.service";
import EventLogs from "@/apps/merchant3/types/EventLogs";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";

// Helper function to check if user is authenticated
const isUserAuthenticated = (): boolean => {
  return !!sessionStorage.getItem(SessionStorageEnum.ClerkJwt);
};

const { t, locale } = useI18n();
const store = useStore();
const route = useRoute();
const { computedFeatureFlag } = useFeatureFlag();

const currentPage = route.path;

const isSmallScreen = ref<boolean>(window.innerWidth <= 768);
const isLoading = ref<boolean>(true);
const previousContent = ref<string>();
const inConsoleContent = ref<Array<IConsoleArticle>>([]);
const showing = ref<string>();

const isInConsoleMessagesFeatureFlagEnabled = computedFeatureFlag(
  AvailableFeatureFlags.IN_CONSOLE_MESSAGES
);

const inConsoleTrainingExperiment = computed(() => {
  return store.state.carts?.experiments?.InConsoleTraining
    ? store.state.carts?.experiments?.InConsoleTraining
    : false;
});

const isEnabled = computed(() => {
  const enabledAndShowingMessages =
    isInConsoleMessagesFeatureFlagEnabled.value &&
    showing.value === SidebarContentTypes.IN_CONSOLE_MESSAGES;
  const enabledAndShowingTraining =
    inConsoleTrainingExperiment.value &&
    showing.value === SidebarContentTypes.IN_CONSOLE_TRAINING;

  return enabledAndShowingMessages || enabledAndShowingTraining;
});

const contentToShow = computed(
  () => store.getters["ui/sidebarState"].contentToShow
);
const merchant: ComputedRef<IMerchant | null> = computed(
  () => store.getters["merchant/merchantState"].merchant
);
const sidebarIsVisible = computed(() => {
  //If is a small screen, the sidebar is closed
  const defaultOpen = !isSmallScreen.value;
  
  const storeState = store.getters["ui/sidebarState"].sidebarIsOpen;
  return storeState !== undefined ? storeState : defaultOpen;
});

// Handle body overflow separately from computed property
watchEffect(() => {
  const isOpen = sidebarIsVisible.value;
  const body = document.querySelector<HTMLElement>("body");

  if (isSmallScreen.value && body) {
    body.style.overflow = isOpen ? "hidden" : "auto";
  }
});

// Initialize sidebar state if undefined
watchEffect(() => {
  if (store.getters["ui/sidebarState"].sidebarIsOpen === undefined) {
    store.dispatch("ui/setInConsoleSidebarState", {
      sidebarIsOpen: !isSmallScreen.value,
    });
  }
});

const loadInConsoleMessages = async (firstTime = false) => {
  try {
    showing.value = SidebarContentTypes.IN_CONSOLE_MESSAGES;
    
    // Check if user is authenticated before making API call
    if (!isUserAuthenticated()) {
      isLoading.value = false;
      inConsoleContent.value = [];
      // Fall back to training content if available
      if (inConsoleTrainingExperiment.value) {
        await loadInConsoleTraining(firstTime);
      }
      return;
    }

    const daysInThePast = 90;

    await getInConsoleMessages(daysInThePast).then((response: AxiosResponse) => {
      const messages: Array<IConsoleArticle> = response.data?.messages.sort(
        (a, b) => {
          if (a.isFeatured !== b.isFeatured) {
            return a.isFeatured ? -1 : 1;
          }

          return new Date(b.date).getTime() - new Date(a.date).getTime();
        }
      );

      /**
       * The latest featured message (first of the list) is already displayed in the InConsoleFeaturedMessage component.
       * So, we remove it from the list of messages to avoid duplication.
       */
      if (messages[0]?.isFeatured) {
        messages.shift();
      }

      if (messages.length === 0) {
        store.dispatch("ui/setInConsoleSidebarState", {
          sidebarIsOpen: false,
          contentToShow: SidebarContentTypes.IN_CONSOLE_MESSAGES,
        });
      }

      isLoading.value = false;
      inConsoleContent.value = messages;

      if (
        previousContent.value !== undefined &&
        previousContent.value !== SidebarContentTypes.IN_CONSOLE_MESSAGES
      ) {
        previousContent.value = SidebarContentTypes.IN_CONSOLE_MESSAGES;

        store.dispatch("ui/setInConsoleSidebarState", {
          sidebarIsOpen: !(firstTime && isSmallScreen.value),
          contentToShow: SidebarContentTypes.IN_CONSOLE_MESSAGES,
        });
      }
    });
  } catch (error: any) {
    isLoading.value = false;
    inConsoleContent.value = [];
    
    // Handle authentication errors gracefully
    if (error.response?.status === 401) {
      // User is not authenticated, close sidebar and fall back to training
      store.dispatch("ui/setInConsoleSidebarState", {
        sidebarIsOpen: false,
        contentToShow: SidebarContentTypes.IN_CONSOLE_TRAINING,
      });
      // Try to load training content instead
      if (inConsoleTrainingExperiment.value) {
        await loadInConsoleTraining(firstTime);
      }
    }
  }
};

const loadInConsoleTraining = async (firstTime = false) => {
  try {
    showing.value = SidebarContentTypes.IN_CONSOLE_TRAINING;
    let country;

    if (merchant.value !== null) {
      country = merchant.value.country;
    } else {
      country = locale.value.split("_")[1];
      country = country === "US" ? "ZA" : country;
    }

    if (!country) {
      isLoading.value = false;
      inConsoleContent.value = [];

      if (previousContent.value !== SidebarContentTypes.IN_CONSOLE_TRAINING) {
        store.dispatch("ui/setInConsoleSidebarState", {
          sidebarIsOpen: !(firstTime && isSmallScreen.value),
          contentToShow: SidebarContentTypes.IN_CONSOLE_TRAINING,
        });
      }

      return;
    }

    const messages: Array<IConsoleArticle> = getTrainingArticles(
      country,
      currentPage
    );

    isLoading.value = false;
    inConsoleContent.value = messages;

    if (previousContent.value !== SidebarContentTypes.IN_CONSOLE_TRAINING) {
      previousContent.value = SidebarContentTypes.IN_CONSOLE_TRAINING;

      store.dispatch("ui/setInConsoleSidebarState", {
        sidebarIsOpen: !(firstTime && isSmallScreen.value),
        contentToShow: SidebarContentTypes.IN_CONSOLE_TRAINING,
      });
    }
  } catch (error: any) {
    isLoading.value = false;
    inConsoleContent.value = [];
  }
};

const loadInConsoleContent = async (firstTime = false) => {
  try {
    isLoading.value = true;

    if (
      previousContent.value === undefined &&
      currentPage.includes("/store") &&
      currentPage.endsWith("/welcome")
    ) {
      await loadInConsoleMessages(firstTime);
    } else if (
      contentToShow.value === undefined ||
      contentToShow.value === SidebarContentTypes.IN_CONSOLE_TRAINING
    ) {
      await loadInConsoleTraining(firstTime);
    } else {
      await loadInConsoleMessages(firstTime);
    }

    isLoading.value = false;
  } catch (error: any) {
    isLoading.value = false;
  }
};

const closeInConsoleMessageSidebarState = () => {
  logEvent(EventLogs.Events.TRAINING_ARTICLES_HIDDEN);

  store.dispatch("ui/setInConsoleSidebarState", {
    sidebarIsOpen: false,
    contentToShow: store.getters["ui/sidebarState"].contentToShow,
  });
};

const adjustListHeight = () => {
  const header = document.querySelector<HTMLElement>(".light-header");
  const headerClerk = document.querySelector<HTMLElement>(".clerk-info-bar");
  const listElement = document.querySelector<HTMLElement>(
    ".in-console-sidebar"
  );

  if (!listElement) return;

  const headerHeight = header?.offsetHeight ?? 0;
  const headerClerkHeight = headerClerk?.offsetHeight ?? 0;
  const validH = Math.max(0, window.innerHeight - (headerHeight + headerClerkHeight));

  listElement.style.height = `${validH}px`;
};

watch(contentToShow, async () => {
  if (
    previousContent.value !== undefined &&
    contentToShow.value !== previousContent.value
  ) {
    await loadInConsoleContent();
  }
});

watch(merchant, async () => {
  if (
    merchant.value !== null &&
    showing.value === SidebarContentTypes.IN_CONSOLE_TRAINING
  ) {
    await loadInConsoleTraining();
  }

  adjustListHeight();
});

onUpdated(() => {
  // Use nextTick to ensure DOM is fully updated
  nextTick(() => {
    adjustListHeight();
  });
});

onMounted(() => {
  window.addEventListener("resize", adjustListHeight);

  loadInConsoleContent(true);
});

onBeforeUnmount(() => {
  store.dispatch("ui/setInConsoleSidebarState", {
    sidebarIsOpen: false,
    contentToShow: undefined,
  });

  window.removeEventListener("resize", adjustListHeight);
});
</script>

<style lang="scss">
@import "@/assets/scss/constants";

.main-body {
  padding: 0 !important;

  @media screen and (max-width: $tablets) {
    padding: 15px !important;
  }
}

.in-console-sidebar {
  background: $pj-color-white;
  width: 0;
  transition: all 0.5s;
  flex-shrink: 0;
  position: relative;
  overflow-y: scroll;
  z-index: 1;

  @media screen and (max-width: $tablets) {
    position: absolute;
    transform: translateY(-15px);
    right: 0;
  }

  > * {
    display: none;
  }

  &--show {
    width: 335px;
    box-shadow: 0 4px 16px 0 rgba(228, 228, 228, 0.5);

    @media screen and (max-width: $tablets) {
      width: 100%;
    }
  }

  &__title-actions {
    height: 40px;
    padding: 0 15px 0 26px;
    border-bottom: 2px solid $pj-color-light-gray;
    position: sticky;
    top: 0;
    z-index: 3;
    background: $pj-color-white;

    &--visible {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
  }

  &__title {
    margin: 0;
    font-size: 14px;
    font-style: normal;
  }

  &__close {
    cursor: pointer;
  }

  &__messages-container {
    display: none;
    flex: 1 1 0;
    overflow-y: scroll;

    &--visible {
      display: block;
    }

    &__messages-list {
      padding: 0;

      &--visible {
        display: flex;
        flex-direction: column;
      }
    }

    &__empty {
      display: flex;
      flex-direction: column;
      text-align: center;
      font-size: 14px;
      font-style: normal;
      font-weight: 500;
      line-height: 17px;
      height: 100%;
      justify-content: center;

      p:first-child {
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 24px;
        margin-bottom: 7px;
      }
    }
  }
}
</style>
